import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { ModalService, CLModalType, CLNotificationType } from '@clavisco/alerts';
import { AuthenticationService } from '../core/services/authentication.service';
import { NotificationCenterService } from '@clavisco/notification-center';

/**
 * ErrorInterceptor intercepts HTTP requests to handle errors and provide user feedback.
 * It displays notifications and modals for various HTTP error statuses.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  /**
   * URLs to exclude from error handling.
   */
  private readonly excludeURLs = ['token'];

  /**
   * Constructs the ErrorInterceptor.
   *
   * @param {AuthenticationService} auth - Service for managing authentication operations.
   * @param {ModalService} modalService - Service for displaying modals.
   * @param {NotificationCenterService} notificationCenterService - Service for managing notifications.
   */
  constructor(
    public auth: AuthenticationService,
    private modalService: ModalService,
    private notificationCenterService: NotificationCenterService
  ) { }

  /**
   * Intercepts HTTP requests and handles errors.
   *
   * @param {HttpRequest<unknown>} request - The outgoing HTTP request.
   * @param {HttpHandler} next - The next interceptor in the chain.
   * @returns {Observable<HttpEvent<unknown>>} An observable of the HTTP event.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError((error: any) => {
      if (this.ShouldExclude(error.url)) {
        return throwError(() => error);
      }
      this.HandleError(error);
      return throwError(() => error);
    }));
  }

  /**
   * Handles HTTP errors by displaying appropriate notifications and modals.
   *
   * @param {any} error - The HTTP error object.
   */
  private HandleError(error: any): void {

    let errorMessage: string = this.GetErrorMessage(error);

    switch (error.status) {
      case 0:
        this.NotifyError('Sin conexión con el servidor', undefined, CLModalType.ERROR);
        break;
      case HttpStatusCode.Unauthorized:
        this.NotifyError('Sin autorización', 'Posiblemente debido a que la sesión es inválida, no tienes permiso para realizar esta acción o las credenciales de SAP son incorrectas', CLModalType.INFO);
        break;
      case HttpStatusCode.NotFound:
        this.NotifyError('No se encontraron registros', errorMessage, CLModalType.INFO);
        break;
      case HttpStatusCode.BadRequest:
        this.NotifyError('Solicitud incorrecta', errorMessage, CLModalType.INFO);
        break;
      case HttpStatusCode.Conflict:
        this.NotifyError('Error', errorMessage, CLModalType.ERROR);
        break;
      default:
        this.NotifyError('Error desconocido', errorMessage, CLModalType.ERROR);
    }
  }

  /**
   * Extracts the error message from the HTTP error response.
   *
   * @param {HttpErrorResponse} pError - The HTTP error response.
   * @returns {string} The extracted error message.
   */
  private GetErrorMessage(pError: HttpErrorResponse): string {
    if (!pError.error) return '';

    let errorMessage = pError.error.Message || pError.error.title;

    return this.FormatErrorAndDetailsMessages(errorMessage, this.MapErrorDetails(pError));
  }

  /**
   * Maps error details from the HTTP error response.
   *
   * @param {HttpErrorResponse} pError - The HTTP error response.
   * @returns {string} A formatted string of error details.
   */
  private MapErrorDetails(pError: HttpErrorResponse): string {
    let errorDetailsMessage = '';

    if (pError.error && pError.error.errors) {
      const errorDetails = pError.error.errors;

      const allErrors = Object.keys(errorDetails)
        .map(key => {
          return `${key}: ${errorDetails[key].join(', ')}`;
        })
        .join('\n');

      errorDetailsMessage = ` Details:\n${allErrors}`;
    }

    return errorDetailsMessage;
  }

  /**
   * Formats the error message and details into a single string.
   *
   * @param {string} pErrorMessage - The main error message.
   * @param {string} pErrorDetailsMessage - The detailed error message.
   * @returns {string} The formatted error message.
   */
  private FormatErrorAndDetailsMessages(pErrorMessage: string, pErrorDetailsMessage: string): string {
    return `
      \n\n${decodeURIComponent(pErrorMessage || "")}\n\n
      \n\n
      \n\n${pErrorDetailsMessage ?? 'Not available'}
    `.trim();
  }

  /**
   * Displays a modal and pushes a notification for the error.
   *
   * @param {string} title - The title of the error.
   * @param {string | undefined} subtitle - The subtitle of the error.
   * @param {CLModalType} type - The type of modal to display.
   */
  private NotifyError(title: string, subtitle: string | undefined, type: CLModalType): void {
    this.modalService.Continue({ title, subtitle, type }).subscribe();

    this.PushNotification(title, subtitle);
  }

  /**
   * Pushes a notification for the error.
   *
   * @param {string | undefined} title - The title of the notification.
   * @param {string | undefined} message - The message of the notification.
   */
  private PushNotification(title: string | undefined, message: string | undefined): void {
    let errorMessage = message || "Unknown error";

    this.notificationCenterService.Push({
      type: CLNotificationType.ERROR,
      priority: 'high',
      title: title ?? "Error",
      message: errorMessage
    });
  }

  /**
   * Checks if the URL should be excluded from error handling.
   *
   * @param {string} url - The URL to check.
   * @returns {boolean} True if the URL should be excluded, false otherwise.
   */
  private ShouldExclude(url: string): boolean {
    return this.excludeURLs.some(excluded => url.includes(excluded));
  }
}
